import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ClipStage } from '../components/ClipStage';
import { RecordingLoader } from '../components/RecordingLoader';
import { formatTimecode } from '../../shared/format';
import {
  CLIP_HEIGHT,
  CLIP_WIDTH,
  DEFAULT_CLIP_OPTIONS,
  clipStats,
  planClip,
  type ClipOptions,
} from '../../shared/clip';
import { parseRecording, type PlaybackRecording } from '../../shared/playback';
import type { LoadedRecording } from '../lib/loadRecording';
import { usePlayer } from '../lib/usePlayer';
import { PLAYBACK_SPEEDS, type PlaybackSpeed } from '../../shared/playback';

/**
 * Vertical clip (2026-09-09) — `#/clip`.
 *
 * Two jobs in one view, deliberately:
 *
 *  · **Preview.** Load a round, tune the cut (pace, how hard the silences
 *    are compressed, intro/outro, an excerpt), watch it play in the actual
 *    9:16 frame, and copy the render command for exactly what you see.
 *  · **Render surface.** With `?render=1` the shell disappears and the page
 *    exposes `window.__clip`, which scripts/render-clip.mts drives frame by
 *    frame in a headless browser. Same component, same options, same
 *    output — the preview is not an approximation of the render, it is the
 *    render at a smaller scale.
 *
 * Rendering steps a playhead instead of watching a clock, so nothing here
 * may depend on wall time; see the note in ClipStage.
 */
export function ClipView() {
  const renderMode = new URLSearchParams(location.hash.split('?')[1] ?? '').get('render') === '1';
  return renderMode ? <ClipRenderSurface /> : <ClipPreview />;
}

/* ------------------------------------------------------------- preview -- */

function ClipPreview() {
  const [loaded, setLoaded] = useState<LoadedRecording | null>(null);
  const [options, setOptions] = useState<ClipOptions>(DEFAULT_CLIP_OPTIONS);

  useEffect(() => {
    const url = loaded?.comicUrl;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [loaded]);

  if (!loaded)
    return (
      <RecordingLoader
        title="Clip"
        lead={
          <>
            Eine Runde als fertiger 9:16-Film —
            <br />
            ohne Kamera, ohne Schnitt.
          </>
        }
        hint={<ClipSteps />}
        onLoaded={setLoaded}
      />
    );

  return (
    <ClipWorkbench
      loaded={loaded}
      options={options}
      setOptions={setOptions}
      onEject={() => setLoaded(null)}
    />
  );
}

function ClipWorkbench({
  loaded,
  options,
  setOptions,
  onEject,
}: {
  loaded: LoadedRecording;
  options: ClipOptions;
  setOptions: (o: ClipOptions) => void;
  onEject: () => void;
}) {
  const rec = loaded.recording;
  const plan = useMemo(() => planClip(rec, options), [rec, options]);
  const source = useMemo(() => ({ totalMs: plan.outDurationMs, gaps: [] }), [plan]);
  const player = usePlayer(source);
  const stats = useMemo(() => clipStats(rec), [rec]);
  const fit = useFitScale();

  const patch = (p: Partial<ClipOptions>) => setOptions({ ...options, ...p });

  return (
    <div className="clipview">
      <div className="clipview__screen" ref={fit.ref}>
        <div className="clipview__scaler" style={{ '--clip-fit': fit.scale } as React.CSSProperties}>
          <ClipStage rec={rec} plan={plan} outMs={player.playheadMs} comicSrc={loaded.comicUrl} />
        </div>
      </div>

      <div className="play__bar">
        <div className="scrub">
          <div className="scrub__track">
            <div className="scrub__done" style={{ width: `${(player.playheadMs / plan.outDurationMs) * 100}%` }} />
            {plan.spans
              .filter((s) => s.idle)
              .map((s) => (
                <span
                  key={s.outFrom}
                  className="scrub__mark scrub__mark--pass"
                  style={{ left: `${((plan.introMs + s.outFrom) / plan.outDurationMs) * 100}%` }}
                  title="Leerlauf, gerafft"
                />
              ))}
            <div className="scrub__head" style={{ left: `${(player.playheadMs / plan.outDurationMs) * 100}%` }} />
          </div>
          <input
            className="scrub__input"
            type="range"
            min={0}
            max={plan.outDurationMs}
            step={40}
            value={player.playheadMs}
            onChange={(e) => player.seek(Number(e.target.value))}
            aria-label="Position im Clip"
            data-testid="clip-scrub"
          />
        </div>

        <div className="play__controls">
          <button
            className="btn btn--primary play__toggle"
            onClick={player.toggle}
            data-testid="clip-toggle"
          >
            {player.playing ? '❚❚ Pause' : player.finished ? '↻ Nochmal' : '▶ Vorschau'}
          </button>
          <div className="play__time" data-testid="clip-time">
            <strong>{formatTimecode(player.playheadMs)}</strong>
            <span>/ {formatTimecode(plan.outDurationMs)}</span>
          </div>
          <div className="play__group play__group--speed">
            {PLAYBACK_SPEEDS.map((s) => (
              <button
                key={s}
                className={`btn btn--small ${player.speed === s ? 'btn--confirmed' : 'btn--ghost'}`}
                onClick={() => player.setSpeed(s as PlaybackSpeed)}
                title="Nur die Vorschau — nicht der gerenderte Clip"
              >
                {s}×
              </button>
            ))}
          </div>
          <div className="play__meta">
            <strong>
              {rec.kind === 'comic' ? 'Bildrunde' : 'Runde'} {rec.roundNumber} · {formatTimecode(plan.outDurationMs)}
            </strong>
            <span>
              {rec.authors.map((a) => a.name).join(' × ')} · {stats.words} Wörter ·{' '}
              {formatTimecode(plan.compressedMs)} Leerlauf gerafft
            </span>
          </div>
          <button className="btn btn--small btn--ghost" onClick={onEject} data-testid="clip-eject">
            Andere Aufnahme
          </button>
        </div>

        <div className="clipview__opts">
          <label>
            Tempo
            <input
              type="number"
              min={0.25}
              max={8}
              step={0.25}
              value={options.speed}
              onChange={(e) => patch({ speed: Number(e.target.value) || 1 })}
              data-testid="clip-speed"
            />
            ×
          </label>
          <label>
            Leerlauf ab
            <input
              type="number"
              min={0}
              max={60}
              step={0.5}
              value={options.idleThresholdMs / 1000}
              onChange={(e) => patch({ idleThresholdMs: Math.max(0, Number(e.target.value)) * 1000 })}
            />
            s
          </label>
          <label>
            gerafft auf
            <input
              type="number"
              min={0.1}
              max={5}
              step={0.1}
              value={options.idleHoldMs / 1000}
              onChange={(e) => patch({ idleHoldMs: Math.max(100, Number(e.target.value) * 1000) })}
            />
            s
          </label>
          <label>
            <input type="checkbox" checked={options.intro} onChange={(e) => patch({ intro: e.target.checked })} />
            Titel + Aufgabe
          </label>
          <label>
            <input type="checkbox" checked={options.outro} onChange={(e) => patch({ outro: e.target.checked })} />
            Endkarte
          </label>
          <label>
            Ausschnitt ab
            <input
              type="number"
              min={0}
              step={5}
              value={Math.round((options.range?.fromMs ?? 0) / 1000)}
              onChange={(e) => patch({ range: rangeFrom(options, Number(e.target.value) * 1000, rec) })}
            />
            s
          </label>
          <label>
            bis
            <input
              type="number"
              min={0}
              step={5}
              value={Math.round((options.range?.toMs ?? rec.roundMs) / 1000)}
              onChange={(e) => patch({ range: rangeTo(options, Number(e.target.value) * 1000, rec) })}
            />
            s
          </label>
          <button className="btn btn--small btn--ghost" onClick={() => setOptions(DEFAULT_CLIP_OPTIONS)}>
            Zurücksetzen
          </button>
        </div>

        <CommandLine command={`npm run clip -- ${loaded.fileName}${renderArgs(options, rec)}`} />
      </div>
    </div>
  );
}

function rangeFrom(options: ClipOptions, fromMs: number, rec: PlaybackRecording) {
  const toMs = options.range?.toMs ?? rec.roundMs;
  return fromMs <= 0 && toMs >= rec.roundMs ? null : { fromMs, toMs };
}
function rangeTo(options: ClipOptions, toMs: number, rec: PlaybackRecording) {
  const fromMs = options.range?.fromMs ?? 0;
  return fromMs <= 0 && toMs >= rec.roundMs ? null : { fromMs, toMs };
}

/** The command line for exactly the cut on screen — copy, paste, render. */
function renderArgs(o: ClipOptions, rec: PlaybackRecording): string {
  const args: string[] = [];
  if (o.speed !== 1) args.push(`--speed ${o.speed}`);
  if (o.idleThresholdMs !== DEFAULT_CLIP_OPTIONS.idleThresholdMs) args.push(`--idle-from ${o.idleThresholdMs / 1000}`);
  if (o.idleHoldMs !== DEFAULT_CLIP_OPTIONS.idleHoldMs) args.push(`--idle-hold ${o.idleHoldMs / 1000}`);
  if (!o.intro) args.push('--no-intro');
  if (!o.outro) args.push('--no-outro');
  if (o.range) args.push(`--range ${Math.round(o.range.fromMs / 1000)}-${Math.round(o.range.toMs / 1000)}`);
  void rec;
  return args.length === 0 ? '' : ` ${args.join(' ')}`;
}

/**
 * How this mode is used, in four steps. Worth spelling out inside the app:
 * the preview runs in the browser but the *render* runs on the machine that
 * has the project, so somebody opening this page on the live site would
 * otherwise sit and wait for a download button that never appears.
 */
function ClipSteps() {
  return (
    <>
      <li>
        <strong>1 · Runde laden.</strong> Das Zip, das die Moderation nach der Runde heruntergeladen hat
        (<code>schreibarena-…-runde-N.zip</code>). Bei einer Bildrunde liegt der Comic im Zip schon dabei.
      </li>
      <li>
        <strong>2 · Schnitt einstellen.</strong> Tempo, ab wann Stille gerafft wird, Titel- und Endkarte — und für
        einen Ausschnitt: von wann bis wann. Die Vorschau ist genau das, was am Ende herauskommt.
      </li>
      <li>
        <strong>3 · Befehl kopieren.</strong> Unter der Vorschau steht der Befehl für exakt diesen Schnitt.
      </li>
      <li>
        <strong>4 · Rendern.</strong> Den Befehl im Projektordner ausführen. Heraus kommt der Ordner{' '}
        <code>clips/</code> mit dem Film — als MP4, wenn <code>ffmpeg</code> installiert ist, sonst als
        Einzelbilder, die jedes Schnittprogramm importiert.
      </li>
      <li className="playhint__note">
        Gerendert wird auf dem Rechner mit dem Projekt, nicht hier im Browser: diese Seite stellt den Schnitt ein
        und zeigt ihn. Hochgeladen wird ohnehin nichts — die Aufnahme bleibt auf dem Gerät.
      </li>
    </>
  );
}

/** The render command for the cut on screen, ready to copy. */
function CommandLine({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      setCopied(false); // no clipboard permission — the text is selectable anyway
    }
  };
  return (
    <div className="clipview__cmdrow">
      <span className="clipview__cmdlabel">Rendern im Projektordner</span>
      <code className="clipview__cmd" data-testid="clip-command">
        {command}
      </code>
      <button className="btn btn--small btn--ghost" onClick={() => void copy()} data-testid="clip-copy">
        {copied ? '✓ Kopiert' : 'Kopieren'}
      </button>
    </div>
  );
}

/** Scale the 1080 × 1920 frame down to whatever the window has left over. */
function useFitScale() {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.3);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const pad = 32;
      setScale(Math.max(0.05, Math.min((box.width - pad) / CLIP_WIDTH, (box.height - pad) / CLIP_HEIGHT)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, scale };
}

/* ------------------------------------------------------- render surface -- */

/** What scripts/render-clip.mts calls. Kept tiny and synchronous on purpose. */
export interface ClipBridge {
  ready: true;
  load(input: {
    files: { name: string; data: unknown }[];
    comicSrc: string | null;
    options: Partial<ClipOptions>;
    fps: number;
    cta: string;
  }): { outDurationMs: number; frames: number; roundNumber: number; kind: string; compressedMs: number };
  /** Show frame `n`; the surface's data-clip-frame flips once it is painted. */
  seekFrame(n: number): void;
}

function ClipRenderSurface() {
  const [job, setJob] = useState<{
    rec: PlaybackRecording;
    options: Partial<ClipOptions>;
    fps: number;
    comicSrc: string | null;
    cta: string;
  } | null>(null);
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const bridge: ClipBridge = {
      ready: true,
      load: (input) => {
        const rec = parseRecording(input.files);
        const plan = planClip(rec, input.options);
        setJob({
          rec,
          options: input.options,
          fps: input.fps,
          comicSrc: input.comicSrc,
          cta: input.cta,
        });
        setFrame(0);
        return {
          outDurationMs: plan.outDurationMs,
          frames: Math.max(1, Math.round((plan.outDurationMs / 1000) * input.fps)),
          roundNumber: rec.roundNumber,
          kind: rec.kind,
          compressedMs: plan.compressedMs,
        };
      },
      seekFrame: (n) => setFrame(n),
    };
    (window as unknown as { __clip: ClipBridge }).__clip = bridge;
  }, []);

  const plan = useMemo(() => (job ? planClip(job.rec, job.options) : null), [job]);

  return (
    <div className="cliprender" data-clip-frame={job ? frame : -1} data-testid="clip-render">
      {job && plan && (
        <ClipStage
          rec={job.rec}
          plan={plan}
          outMs={(frame * 1000) / job.fps}
          comicSrc={job.comicSrc}
          cta={job.cta}
        />
      )}
    </div>
  );
}
